import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React, { useEffect } from "react";
import { useHistory } from "react-router";
import { ConsoleRoleType } from "types/graphqlapi";

import {
  DisplaySubject,
  useOptionGroupList,
  useSubjectList,
  useUserList,
} from "./hook";
import {
  StyledHeadline,
  StyledSubjectManagementContainer,
  StyledUl,
  StyledLi,
  StyledButtonArea,
  StyledFilterArea,
  StyledSubjectListWrapper,
  StyledGroupFilter,
  StyledCross,
} from "./styled";
import { Typography } from "components/parts/Typography/Typography";
import { Button } from "components/parts/Button/Button";
import { StatusButton } from "components/parts/Button/StatusButton";
import { SubjectList } from "pages/SubjectManagement/SubjectList";
import { SubjectManageModal } from "pages/SubjectManagement/SubjectManageModal";
import { TextWithIconButton } from "components/parts/Button/TextWithIconButton";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { SelectBox } from "components/parts/Input/SelectBox";

// ステータスフィルター
const TABLE_HEADER_FILTER_STATUS_LIST = [
  { text: "すべて", value: "ALL" },
  { text: "未実施", value: "NOT_STARTED" },
  { text: "実施中", value: "DOING" },
  { text: "実施済", value: "DONE" },
] as const;

// チェックされているかのフィルター
const TABLE_HEADER_FILTER_IS_MARKED_LIST = [
  { text: "すべて", value: "ALL" },
  { text: "スター付き", value: "MARKED" },
] as const;

export const SubjectManagement: React.FC = () => {
  const { setLoading } = useLoadingState();
  const {
    role,
    setSubject,
    setGroup,
    subjectFilter,
    setSubjectFilter,
    isOneGroupUser,
  } = useConsoleState();
  const {
    displaySubjectList,
    markedSubjectList,
    fetchSubjects,
    fetchMarkedSubjects,
    onChangeSubjectMark,
  } = useSubjectList();
  const { optionGroupList } = useOptionGroupList();
  const [selectedSubject, setSelectedSubject] =
    React.useState<DisplaySubject>();

  useEffect(() => {
    setSubject(undefined);
    if (
      role === ConsoleRoleType.ADMINISTRATOR ||
      role === ConsoleRoleType.ASMARQUSER
    ) {
      setGroup("", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clearSelectedSubject = () => {
    setSelectedSubject(undefined);
    setLoading(false);
    fetchMarkedSubjects();
    fetchSubjects();
  };
  const selectSubject = (subject: DisplaySubject) => {
    setSelectedSubject(subject);
  };
  const createNewSubject = () => {
    selectSubject({} as DisplaySubject);
  };
  const history = useHistory();
  const moveUserManage = () => {
    history.replace(`/user`);
  };
  const moveGroupManage = () => {
    history.replace(`/group`);
  };

  const { asmarqUserList, optionGroupUserList } = useUserList();

  return (
    <>
      <StyledSubjectManagementContainer>
        <StyledHeadline>
          <Typography size="xxlarge" weight={"bold"} text="案件" />
          <StyledButtonArea>
            <TextWithIconButton
              onClick={createNewSubject}
              text="案件を作成"
              iconName={TextWithIconTypes.Plus}
            />
            {role === ConsoleRoleType.GROUPUSER ? (
              <></>
            ) : (
              <Button
                onClick={moveUserManage}
                text="ユーザー"
                variant="white"
              />
            )}
            {role === ConsoleRoleType.ADMINISTRATOR ? (
              <Button
                onClick={moveGroupManage}
                text="グループ"
                variant="white"
              />
            ) : (
              <></>
            )}
          </StyledButtonArea>
        </StyledHeadline>
        <StyledFilterArea>
          <StyledUl>
            <Typography text="ステータス：" size="small" />
            {TABLE_HEADER_FILTER_STATUS_LIST.map((status) => {
              return (
                <StyledLi key={status.value}>
                  <StatusButton
                    text={status.text}
                    isActive={subjectFilter.status === status.value}
                    onClick={() => {
                      setSubjectFilter((prev) => {
                        return { ...prev, status: status.value };
                      });
                    }}
                    size="small"
                  />
                </StyledLi>
              );
            })}
          </StyledUl>
          <StyledUl>
            <Typography text="スター付き：" size="small" />
            {TABLE_HEADER_FILTER_IS_MARKED_LIST.map((isMarked) => {
              return (
                <StyledLi key={isMarked.value}>
                  <StatusButton
                    text={isMarked.text}
                    isActive={subjectFilter.marked === isMarked.value}
                    onClick={() => {
                      setSubjectFilter((prev) => {
                        return { ...prev, marked: isMarked.value };
                      });
                    }}
                    size="small"
                  />
                </StyledLi>
              );
            })}
          </StyledUl>
          {!isOneGroupUser && (
            <StyledGroupFilter>
              <Typography text="グループ：" size="small" />
              {!subjectFilter.isSelectGroup && (
                <SelectBox
                  type="filter"
                  value={subjectFilter.group}
                  optionList={[
                    { value: "ALL", text: "すべて" },
                    ...optionGroupList,
                  ]}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    const name = e.currentTarget.selectedOptions[0].innerHTML;
                    setSubjectFilter((prev) => {
                      return {
                        ...prev,
                        group: value,
                        groupName: name,
                      };
                    });
                  }}
                />
              )}
              <StatusButton
                text={
                  subjectFilter.isSelectGroup && subjectFilter.group ? (
                    <>
                      {subjectFilter.groupName}
                      <StyledCross />
                    </>
                  ) : (
                    "選択"
                  )
                }
                onClick={() => {
                  setSubjectFilter((prev) => {
                    return {
                      ...prev,
                      isSelectGroup: !prev.isSelectGroup,
                      ...(subjectFilter.isSelectGroup ? { group: "ALL" } : {}),
                    };
                  });
                }}
                size="small"
                isActive={subjectFilter.isSelectGroup}
                outlined={!subjectFilter.isSelectGroup}
                disabled={subjectFilter.group === "ALL"}
              />
            </StyledGroupFilter>
          )}
        </StyledFilterArea>
        <StyledSubjectListWrapper>
          {displaySubjectList ? (
            <SubjectList
              subjectList={displaySubjectList}
              selectSubject={selectSubject}
              markedSubjectList={markedSubjectList}
              onChangeSubjectMark={onChangeSubjectMark}
            />
          ) : (
            "案件がありません"
          )}
        </StyledSubjectListWrapper>
        {selectedSubject && (
          <SubjectManageModal
            closeHandler={clearSelectedSubject}
            selectedSubject={selectedSubject}
            clearSelectedSubject={clearSelectedSubject}
            optionGroupUserList={optionGroupUserList}
            asmarqUserList={asmarqUserList}
            optionGroupList={optionGroupList}
          />
        )}
      </StyledSubjectManagementContainer>
    </>
  );
};
